import type { AnnouncerData, AnnouncerPhraseData } from '../api/adminApi';
import type { AnnouncerBundle } from '../api/announcerApi';
import { pickQuote } from '../data/resultQuotes';

// ─────────────────────────────────────────────────────────────────────────────
// Sorteio do locutor + fala do quadro de fim de batalha.
// Vitória → locutor do reino ('hero'); derrota → locutor monstro ('monster').
// Não repete o último locutor nem a última fala de cada tipo. Sem falas
// cadastradas (ou API fora) → frases embutidas de data/resultQuotes.
// ─────────────────────────────────────────────────────────────────────────────

export type ResultKind = 'victory' | 'defeat';

const last: Record<string, string | undefined> = {};

function pickNoRepeat<T extends { id?: string }>(list: T[], key: string, rand: () => number): T | null {
  if (list.length === 0) return null;
  let i = Math.floor(rand() * list.length) % list.length;
  if (list.length > 1 && list[i].id === last[key]) i = (i + 1) % list.length;
  last[key] = list[i].id;
  return list[i];
}

/** Troca {jogador} e {monstro} (sem nome → some o marcador com um termo neutro). */
export function fillLine(text: string, vars: { jogador?: string; monstro?: string }, lang: string): string {
  return text
    .replace(/\{jogador\}/g, vars.jogador || (lang === 'en' ? 'hero' : 'herói'))
    .replace(/\{monstro\}/g, vars.monstro || (lang === 'en' ? 'the monster' : 'o monstro'));
}

export function pickAnnouncerLine(
  bundle: AnnouncerBundle | null,
  kind: ResultKind,
  lang: string,
  vars: { jogador?: string; monstro?: string },
  rand: () => number = Math.random,
): { announcer: AnnouncerData | null; text: string } {
  const side = kind === 'victory' ? 'hero' : 'monster';
  const announcers = (bundle?.announcers ?? []).filter(a => a.active && a.side === side);
  const phrases = (bundle?.phrases ?? []).filter((p: AnnouncerPhraseData) => p.active && p.kind === kind);

  const announcer = pickNoRepeat(announcers, `a:${side}`, rand);
  const phrase = pickNoRepeat(phrases, `p:${kind}`, rand);
  const raw = phrase
    ? (lang === 'en' && phrase.text_en ? phrase.text_en : phrase.text_pt)
    : pickQuote(kind, lang, rand);
  return { announcer, text: fillLine(raw, vars, lang) };
}
